import { useState } from "react";
import { Create, required, SaveButton, SimpleForm, TextInput, Toolbar, useNotify } from "react-admin";

import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import { Typography, Grid, Button, Dialog, DialogContent } from "@mui/material";

import { getLabel } from "./labels";
import { getToken } from "auth/dataProvider.utils";
import { errorMessages, settings } from "app";
import { studentRequests } from "api/studentApi";
import ModalTitle, {
  withModalSx,
  modalCancelButtonSx,
  modalConfirmButtonSx,
} from "server/components/Modals/ModalTitle";

const StudentCreditRequestDialog = ({ record, disabled }) => {
  const notify = useNotify();
  const [isOpen, setIsOpen] = useState(false);
  const [existData, setExistData] = useState(null);

  const url = `${settings.gatewayApi}/${studentRequests.getAcademicRequestDocumentDownload(record.id)}`;

  const openBlobPdf = blob => {
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank");
  };

  const handleError = async res => {
    const errorJson = await res.json();

    if (errorJson?.properties) {
      setExistData(errorJson.properties);
      setIsOpen(true);
      return;
    }

    notify(errorMessages[errorJson.errorCode] || "Възникна грешка!", {
      type: "error",
    });
  };

  const fetchPdf = async body => {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Session-Id": getToken(),
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      await handleError(res);
      return null;
    }

    return await res.blob();
  };

  const openPdf = async () => {
    try {
      const blob = await fetchPdf({});
      if (!blob) return;

      openBlobPdf(blob);
      notify("Успешно генериран документ", { type: "success" });
    } catch (error) {
      notify("Грешка при изтегляне на PDF", { type: "error" });
      console.error(error);
    }
  };

  const submitMissingData = async values => {
    try {
      const blob = await fetchPdf({ data: values });
      if (!blob) return;

      openBlobPdf(blob);
      notify("Успешно генериран документ", { type: "success" });
      setIsOpen(false);
    } catch (error) {
      notify("Грешка при генериране на PDF!", { type: "error" });
      console.error(error);
    }
  };

  const TitleRow = ({ title }) => (
    <Typography
      color="secondary"
      margin="1rem auto 2rem auto"
      fontSize={14}
      paddingBottom={2}
    >
      {title}
    </Typography>
  );

  const RequestForm = () => (
    <>
      <TitleRow title="Моля попълнете полетата, за да бъде генериран документ." />
      <Grid
        container
        spacing={3}
      >
        {existData?.map((field, i) => (
          <Grid
            item
            xs={6}
            key={i}
          >
            <TextInput
              source={field}
              label={getLabel(field)}
              validate={required()}
              fullWidth
            />
          </Grid>
        ))}
      </Grid>
    </>
  );

  const PostCreateToolbar = () => (
    <Toolbar
      sx={{
        display: "flex",
        justifyContent: "space-between",
        padding: "1.5rem",
      }}
    >
      <Button
        onClick={() => setIsOpen(false)}
        variant={settings.style.modernUi ? "outlined" : "contained"}
        color="error"
        size="small"
        startIcon={<CloseIcon />}
        sx={modalCancelButtonSx}
      >
        Затвори
      </Button>

      <SaveButton
        label="Запази"
        variant="contained"
        sx={modalConfirmButtonSx}
        mutateOptions={{
          onSuccess: values => submitMissingData(values),
        }}
      />
    </Toolbar>
  );

  return (
    <>
      <Button
        disabled={disabled}
        startIcon={<FileDownloadIcon />}
        size="small"
        sx={{ px: 2 }}
        onClick={openPdf}
      >
        Генерирай
      </Button>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="md"
        fullWidth
        sx={withModalSx()}
      >
        <ModalTitle
          icon={<FileDownloadIcon />}
          title="Необходими данни за издаване на удостоверение за студентски кредит"
        />

        <DialogContent dividers>
          <Create>
            <SimpleForm
              toolbar={<PostCreateToolbar />}
              onSubmit={submitMissingData}
            >
              <RequestForm />
            </SimpleForm>
          </Create>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StudentCreditRequestDialog;
